import { FunctionComponent, useEffect, useMemo, useState } from "react";
import ITrainer, { TrainerTypes } from "../../types/Trainer";
import Trainer from "../Trainer/Trainer";
import S from "./styled";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";
import { NotificationManager } from "react-notifications";
import Utils from "utils";
import Select from "../Select";
import { AllPositions, InfieldPositions, OutfieldPositions, PitcherPositions, Teams } from "types/Player";
import Rarity from "types/Rarity";

const OpenLockIcon = <FontAwesomeIcon icon={faLockOpen} style={{ color: "white" }} />;
const LockIcon = <FontAwesomeIcon icon={faLock} style={{ color: "#ff9a00" }} />;

const rarityOrder = {
  [Rarity.UR]: 0,
  [Rarity.SSR]: 1,
  [Rarity.SR]: 2,
  [Rarity.R]: 3,
  [Rarity.N]: 4,
};

type Props = {
  trainers: ITrainer[];
  locked?: string[];
  selected: ITrainer[];
  maxSelected?: number;
  setSelected: (selected: ITrainer[]) => any;
  setTrainers: (trainers: ITrainer[]) => any;
  setLocked?: (locked: string[]) => any;
  withLocks?: boolean;
};

const TrainerContainer = styled.div`
  position: relative;
  opacity: 0.2;

  &:hover {
    opacity: 0.8;
  }

  ${(props: { selected: boolean }) =>
    props.selected &&
    `
    opacity: 1 !important;
  `}
`;

const TrainerLock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 8px;
  cursor: pointer;
`;

const skipFilter = (value: string[]) => {
  return !Array.isArray(value) || value.length === 0;
};

const Trainers: FunctionComponent<Props> = ({
  trainers,
  locked,
  selected,
  maxSelected,
  setSelected,
  setTrainers,
  setLocked,
  withLocks,
}) => {
  const [team, setTeam] = useState([]);
  const [stat, setStat] = useState([]);
  const [rarity, setRarity] = useState([]);
  const [position, setPosition] = useState([]);

  useEffect(() => {
    const newTrainers = trainers.map((trainer) => {
      const _team = skipFilter(team) || team.some((t) => trainer.bonusTeam?.includes(t.value));
      const _stat = skipFilter(stat) || stat.some((s) => trainer.type.includes(s.value as TrainerTypes));
      const _rarity = skipFilter(rarity) || rarity.some((r) => r.value === (trainer.rarity as Rarity));
      const _position = skipFilter(position) || position.some((p) => p.value === (trainer.position as any));

      const isValid = _team && _stat && _rarity && _position;

      return { ...trainer, disabled: !isValid };
    });

    setTrainers(newTrainers);
  }, [trainers.length, team, stat, rarity, position]);

  const select = (trainer: ITrainer) => {
    const isSelected = selected.some((s) => s.name === trainer.name);

    if (isSelected) {
      if (withLocks) {
        setLocked(locked.filter((t) => t !== trainer.name));
      }

      setSelected(selected.filter((s) => s.name !== trainer.name));
    } else {
      if (maxSelected && selected.length >= maxSelected) {
        NotificationManager.warning(`You can't select more than ${maxSelected} trainers at once.`);
        return;
      }

      setSelected([...selected, trainer]);
    }
  };

  const isSelected = (trainerName: string) => {
    return selected.some((trainer) => trainer.name == trainerName);
  };

  const onLockClick = (trainerName: string) => {
    if (locked.includes(trainerName)) {
      setLocked(locked.filter((t) => t !== trainerName));
    } else {
      setLocked([...locked, trainerName]);
    }
  };

  const onLevelUpdate = (trainer: ITrainer) => {
    setTrainers(Utils.updateTrainers(trainers, [trainer]));

    if (isSelected(trainer.name)) {
      setSelected(Utils.updateTrainers(selected, [trainer]));
    }
  };

  const [positionType, _setPositionType] = useState({ label: "All", value: "All" });

  const setPositionType = ({ value, label }) => {
    _setPositionType({ value, label });

    if (value === "All") {
      setPosition([]);
      return;
    }

    const tags =
      value === "Pitchers" ? Object.keys(PitcherPositions) : Object.keys({ ...OutfieldPositions, ...InfieldPositions });

    const options = tags.map((x) => ({ label: x, value: x }));

    setPosition(options);
  };

  return (
    <S.Container>
      <S.Filters>
        <S.Filter>
          <S.FilterName>Batters/Pitchers</S.FilterName>
          <Select
            value={positionType}
            options={[
              { value: "All", label: "All" },
              { value: "Pitchers", label: "Pitchers" },
              { value: "Batters", label: "Batters" },
            ]}
            onChange={setPositionType}
          />
        </S.Filter>

        <S.Filter>
          <S.FilterName>Position</S.FilterName>
          <Select
            value={position}
            options={[
              { value: "All", label: "All" },
              ...Object.values(AllPositions).map((value) => ({ value, label: value })),
            ]}
            onChange={setPosition}
            isMulti
          />
        </S.Filter>

        <S.Filter>
          <S.FilterName>Stat</S.FilterName>
          <Select
            value={stat}
            options={[
              { value: "All", label: "All" },
              ...Object.values(TrainerTypes).map((value) => ({ value, label: value })),
            ]}
            onChange={setStat}
            isMulti
          />
        </S.Filter>

        <S.Filter>
          <S.FilterName>Rarity</S.FilterName>
          <Select
            value={rarity}
            options={[
              { value: "All", label: "All" },
              ...Object.values(Rarity).map((value) => ({ value, label: value })),
            ]}
            onChange={setRarity}
            isMulti
          />
        </S.Filter>

        <S.Filter>
          <S.FilterName>Team</S.FilterName>
          <Select
            value={team}
            options={[
              { value: "All", label: "All" },
              ...Object.values(Teams).map((value) => ({ value, label: value })),
            ]}
            onChange={setTeam}
            isMulti
          />
        </S.Filter>
      </S.Filters>

      <S.Trainers style={withLocks ? { height: "calc(100% - 50px)" } : {}}>
        {trainers
          .filter((trainer) => !trainer.disabled)
          .sort((a, b) => rarityOrder[a.rarity] - rarityOrder[b.rarity])
          .map((trainer) => (
            <TrainerContainer key={trainer.name} selected={isSelected(trainer.name)}>
              {withLocks && isSelected(trainer.name) && (
                <TrainerLock onClick={onLockClick.bind(null, trainer.name)}>
                  {locked.includes(trainer.name) ? LockIcon : OpenLockIcon}
                </TrainerLock>
              )}
              <Trainer data={trainer} onClick={select} updateLevel={onLevelUpdate} />
            </TrainerContainer>
          ))}
      </S.Trainers>
    </S.Container>
  );
};

export default Trainers;
